import React, { useState } from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import './LoginModal.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
  defaultMode?: 'member' | 'admin';
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess, defaultMode = 'member' }) => {
  const { googleLogin, login } = useAuth();
  const [mode, setMode] = useState<'member' | 'admin'>(defaultMode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoTaps, setLogoTaps] = useState(0);

  // Admin credential form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleLogoClick = () => {
    if (mode === 'admin') { setMode('member'); setLogoTaps(0); setError(null); return; }
    const next = logoTaps + 1;
    setLogoTaps(next);
    if (next >= 10) { setMode('admin'); setLogoTaps(0); setError(null); setUsername(''); setPassword(''); }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError('No credential received from Google');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await googleLogin(credentialResponse.credential);
      onClose();
      if (onLoginSuccess) onLoginSuccess();
    } catch (err: any) {
      setError(err?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Username and password are required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await login({ username, password });
      onClose();
      if (onLoginSuccess) onLoginSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="lm-overlay" onClick={onClose}>
      <div className="lm-content" onClick={(e) => e.stopPropagation()}>
        <button className="lm-close" onClick={onClose} aria-label="Close">×</button>

        {mode === 'member' ? (
          <>
            <div className="lm-logo" onClick={handleLogoClick} style={{ cursor: 'default', userSelect: 'none' }}><span>📈</span></div>
            <h2 className="lm-title">Welcome back</h2>
            <p className="lm-sub">Sign in to access your learning, screeners, and community.</p>

            {error && <div className="lm-error">{error}</div>}

            {isLoading ? (
              <div className="lm-loading-wrap">
                <div className="lm-spinner" />
                <span>Signing you in…</span>
              </div>
            ) : (
              <div className="lm-google-card">
                <div className="lm-google-card-inner">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google sign-in was cancelled or failed.')}
                    useOneTap={false}
                    theme="filled_black"
                    size="large"
                    width="304"
                    text="continue_with"
                    shape="rectangular"
                  />
                </div>
                <div className="lm-google-glow" />
              </div>
            )}

            <p className="lm-note">Members sign in with Google only. No password needed.</p>
          </>
        ) : (
          <>
            <div className="lm-logo" onClick={handleLogoClick} style={{ cursor: 'default', userSelect: 'none' }}><span>🔐</span></div>
            <h2 className="lm-title">Admin Sign In</h2>
            <p className="lm-sub">Use your admin credentials to access the management panel.</p>

            {error && <div className="lm-error">{error}</div>}

            <form className="lm-cred-form" onSubmit={handleAdminLogin}>
              <div className="lm-field">
                <label htmlFor="lm-username">Username</label>
                <input
                  id="lm-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin username"
                  autoComplete="username"
                  autoFocus
                />
              </div>
              <div className="lm-field">
                <label htmlFor="lm-password">Password</label>
                <input
                  id="lm-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
              <button type="submit" className="lm-submit" disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
